import EventManager from '../eventManager/EventManager'
import { addClass, writeCss } from '../helpers/DOM'
import getContentWidth from '../helpers/DOM/getContentWidth'
import { overrideAncestors } from '../helpers/DOM/overrideAncestors'
import { Logger, LogLevel } from '../helpers/Logger'
import Waypoint from '../lazyable/waypoints/WaypointAdapter'
import type WebModel from '../models/WebModel'
import { isNative } from '../prebidFacade/BidderSettings'
import type { Slot } from '../slots/Slot'
import { StickyConfig } from './StickyConfig'

const MEDIAVINE_BRANDING_HEIGHT = 15
const defaultStickyWrapperTop = 10
const PIXEL_BUFFER = 120

const setIntervalTime = 1000
const clearIntervalTimeout = 120000

const debugLogger = new Logger(LogLevel.debug)

export class StickySlot {
  private recalculate = {}
  private slotWaypoints = {}
  private slotStopWaypoints = {}

  constructor(private model: WebModel) {
    EventManager.on(EventManager.events.slotWrapperRenderEnded, (slot) => {
      this.handleSticky(slot)
    })
  }

  // NOTE: Call this in the constructor, after this.adUnitId has been set.
  handleSticky(slot: Slot): Slot {
    const { stickyConfig } = slot
    if (stickyConfig.isSticky) {
      this.wrapSticky(slot)
      this.writePseudoStickyCss(slot)
      EventManager.on(
        EventManager.events.slotRenderEnded,
        (event, eventSlot) => {
          if (eventSlot === slot) {
            this.stickySlotRenderEnded(event, eventSlot)
          }
        }
      )
    }

    return slot
  }

  private writePseudoStickyCss({ stickyConfig, adUnitId }: Slot): string {
    const slotStickyOffset = stickyConfig.offset
    const stickyStyles = `
      #${adUnitId}_sticky_wrapper.stuck {
        position : fixed;
        top : ${
          slotStickyOffset ? slotStickyOffset : defaultStickyWrapperTop
        }px;
      }
    `
    writeCss(stickyStyles)

    return stickyStyles
  }

  private wrapSticky(slot: Slot): void {
    const placeholder = document.createElement('div')
    placeholder.id = `${slot.adUnitId}_placeholder`
    addClass(placeholder, `${slot.adUnitId}_placeholder`)

    const stickyWrapper = document.createElement('div')
    stickyWrapper.id = `${slot.adUnitId}_sticky_wrapper`

    const slotWrapper = slot.wrapper
    wrapElement(slotWrapper, stickyWrapper)
    wrapElement(stickyWrapper, placeholder)
  }

  private computeFullOffset(element: HTMLElement): number {
    let currentElement = element
    let fullOffset = 0
    while (currentElement) {
      fullOffset += currentElement.offsetTop || 0
      currentElement = currentElement.offsetParent as HTMLElement
    }
    return fullOffset
  }

  private computeOffsetDiff(topEl: HTMLElement, bottomEl: HTMLElement): number {
    return this.computeFullOffset(bottomEl) - this.computeFullOffset(topEl)
  }

  private getContainingElement(
    elementToContain: HTMLElement,
    relativeToSelector: string
  ): HTMLElement | undefined {
    let currentElement = elementToContain
    while (currentElement.parentElement) {
      if (currentElement.parentElement.querySelector(relativeToSelector)) {
        return currentElement
      } else {
        currentElement = currentElement.parentElement
      }
    }
  }

  private computeVerticalSpaceAvailable(
    elementToContain: HTMLElement,
    elementContainer: HTMLElement
  ) {
    const containedRect = elementToContain.getBoundingClientRect()
    const containerRect = elementContainer.getBoundingClientRect()

    const containerBottom = containerRect.bottom
    const containedTop = containedRect.top

    let currentSpace = containerBottom - containedTop
    let currentElement = elementToContain

    while (
      currentElement !== elementContainer &&
      currentElement.parentElement
    ) {
      const containedStyles = getComputedStyle(currentElement)
      const containerStyles = getComputedStyle(currentElement.parentElement)

      const unavailableContainedSpace =
        parseInt(containedStyles.marginTop) +
          parseInt(containedStyles.marginBottom) || 0
      const unavailableContainerSpace =
        parseInt(containerStyles.paddingTop) +
          parseInt(containerStyles.paddingBottom) || 0

      currentSpace -= unavailableContainedSpace
      currentSpace -= unavailableContainerSpace

      currentElement = currentElement.parentElement
    }

    return currentSpace
  }

  private computeScrollHeightWithStop(
    stopSelector: string,
    wrapper: HTMLElement,
    placeholder: HTMLElement
  ): number {
    let stickyScrollHeight = 0
    const stopElement: HTMLElement | null = document.querySelector(stopSelector)

    if (stopElement) {
      const offsetDiff = this.computeOffsetDiff(placeholder, stopElement)
      stickyScrollHeight = offsetDiff
      const containingElement = this.getContainingElement(
        placeholder,
        stopSelector
      )
      const containerSize =
        (containingElement &&
          this.computeVerticalSpaceAvailable(placeholder, containingElement)) ||
        0
      if (
        containerSize &&
        containerSize > wrapper.getBoundingClientRect().height
      ) {
        stickyScrollHeight = Math.min(containerSize, offsetDiff)
      }
    }

    return stickyScrollHeight
  }

  private computeScrollHeightWithLeaderboard(placeholder: HTMLElement): number {
    let stickyScrollHeight = 0
    const stopElement = document.getElementById('leaderboard_btf_waypoint')
    if (stopElement) {
      stickyScrollHeight = this.computeOffsetDiff(placeholder, stopElement)
    }
    return stickyScrollHeight
  }

  private computeScrollHeightWithContent(
    contentSelector: string,
    placeholder: HTMLElement
  ): number {
    let stickyScrollHeight = 0

    const content = document.querySelector(contentSelector)
    if (content) {
      const contentBottom = content.getBoundingClientRect().bottom
      const sidebarTop = placeholder.getBoundingClientRect().top

      stickyScrollHeight = contentBottom - sidebarTop
    }

    return stickyScrollHeight
  }

  private computeScrollHeight(
    stickyConfig: StickyConfig,
    wrapper: HTMLElement,
    placeholder: HTMLElement
  ): number {
    let stickyScrollHeight = 0
    if (stickyConfig.stopSelector) {
      stickyScrollHeight = this.computeScrollHeightWithStop(
        stickyConfig.stopSelector,
        wrapper,
        placeholder
      )
    } else if (this.model.leaderboard_btf_selector) {
      // Default to leaderboard_btf_waypoint
      stickyScrollHeight = this.computeScrollHeightWithLeaderboard(placeholder)
    }

    if (!stickyScrollHeight && this.model.content_selector) {
      stickyScrollHeight = this.computeScrollHeightWithContent(
        this.model.content_selector,
        placeholder
      )
    }

    return stickyScrollHeight
  }

  private guardScrollHeight(
    stickyScrollHeight: number,
    placeholder: HTMLElement,
    sizes: AdSizes
  ): number {
    const slotMaxHeight = Math.max(
      ...sizes.map((size) => (Array.isArray(size) && size[1]) || 0)
    )

    const currentPlaceholderHeight = parseInt(
      getComputedStyle(placeholder).height
    )
    if (
      currentPlaceholderHeight &&
      stickyScrollHeight - currentPlaceholderHeight <= slotMaxHeight
    ) {
      stickyScrollHeight = currentPlaceholderHeight
    }

    return stickyScrollHeight
  }

  private setupTrueStickyStyles(
    { stickyConfig, sizes }: Pick<Slot, 'stickyConfig' | 'sizes'>,
    wrapper: HTMLElement,
    placeholder: HTMLElement
  ) {
    let stickyScrollHeight = this.computeScrollHeight(
      stickyConfig,
      wrapper,
      placeholder
    )

    if (!stickyScrollHeight) {
      return
    }

    stickyScrollHeight = this.guardScrollHeight(
      stickyScrollHeight,
      placeholder,
      sizes
    )

    const slotStickyOffset = stickyConfig.offset || defaultStickyWrapperTop

    this.setupCssStickySidebar(
      placeholder,
      wrapper,
      stickyScrollHeight,
      slotStickyOffset
    )
  }

  private getTranslateY(element: HTMLElement) {
    const style = getComputedStyle(element)
    const matrixString: string =
      // @ts-ignore
      style.transform || style.webkitTransform || style.mozTransform || ''

    // matrix style is a string that looks like 'matrix(1, 0, 0, 1, 0, 60)'
    const matrixValuesString = matrixString.match(/matrix.*\((.+)\)/)
    const values =
      matrixValuesString &&
      matrixValuesString[1] &&
      matrixValuesString[1].split(', ')

    // in a simple 2d transform matrix (all that should apply here) the 5th and 6the entries
    // of the matrix are the x and y translate values, and so we return the y translate if it exists
    return (values && values[5] && parseInt(values[5])) || 0
  }

  private setupCssStickySidebar(
    railElement: HTMLElement,
    stickyElement: HTMLElement,
    railHeight: number,
    topOffset: number
  ) {
    overrideAncestors(
      stickyElement,
      { overflow: 'visible' },
      !!this.model.optimize_sticky_sidebar_cls_log
    )

    railElement.style.height = `${railHeight}px`
    stickyElement.style.position = 'sticky'

    stickyElement.style.top = `${topOffset}px`

    const railRect = railElement.getBoundingClientRect()
    const stickyRect = stickyElement.getBoundingClientRect()

    const translateY = this.getTranslateY(stickyElement)

    if (railRect.top === stickyRect.top - translateY) {
      stickyElement.className = ''
    } else {
      stickyElement.className = 'stuck'
    }
  }

  private stickySlotRenderEnded(event, slot: Slot): void | boolean {
    const { adUnitId, stickyConfig, selector, model } = slot
    const wrapper = document.getElementById(
      `${adUnitId}_sticky_wrapper`
    ) as HTMLElement
    const placeholder = document.getElementById(
      `${adUnitId}_placeholder`
    ) as HTMLElement

    if (!selector || !stickyConfig.isSticky) {
      return false
    }
    if (event.isEmpty) {
      return false
    }
    if (placeholder === null) {
      return false
    }

    // Wrapper width should equal sidebar width
    const placeholderParentNode = placeholder.parentNode as HTMLElement
    const minimumSidebarWidth = 300
    wrapper.style.width = `${Math.max(
      getContentWidth(placeholderParentNode),
      minimumSidebarWidth
    )}px`

    if (model.optimize_sticky_sidebar_cls) {
      this.setupTrueStickyStyles(slot, wrapper, placeholder)

      // Auto recalculate rail length every second for 2 minutes. Beyond that they likely left page.
      const sidebarInterval = setInterval(
        () => this.setupTrueStickyStyles(slot, wrapper, placeholder),
        setIntervalTime
      )

      setTimeout(() => {
        clearInterval(sidebarInterval)
      }, clearIntervalTimeout)
    } else {
      this.setupWaypoints(event, slot, wrapper, placeholder)
    }
  }

  private setupWaypoints(
    event,
    slot: Slot,
    wrapper: HTMLElement,
    placeholder: HTMLElement
  ): void | boolean {
    const { stickyConfig } = slot
    this.destroyOldWaypoints(slot)

    // Placeholder should set height to where ad was. Add some height for mediavine branding
    let adHeight = Number(event.size[1])
    if (adHeight === 1 && slot.highestPrebid) {
      // Prebid won, use highestPrebid.height
      adHeight = slot.highestPrebid.height || 300
      if (isNative(slot.highestPrebid)) {
        adHeight = 600
      }
    }
    adHeight += MEDIAVINE_BRANDING_HEIGHT
    placeholder.style.height = `${adHeight}px`

    let stopElement: HTMLElement | null = null
    if (stickyConfig.stopSelector) {
      stopElement = document.querySelector(stickyConfig.stopSelector)
    } else if (this.model.leaderboard_btf_selector) {
      // Default to leaderboard_btf_waypoint
      stopElement = document.getElementById('leaderboard_btf_waypoint')
    }

    const stickyOffset = stickyConfig.offset || 10

    // Waypoint to sticky
    this.slotWaypoints[slot.adUnitId] = new Waypoint({
      element: placeholder,
      handler(direction): void {
        if (direction === 'down') {
          if (stopElement !== null) {
            const stickyScrollHeight =
              stopElement.offsetTop - placeholder.offsetTop
            const minimumStickyScrollHeight =
              placeholder.offsetHeight * 2 + PIXEL_BUFFER

            // Make sticky if enough room. Extra padding in case we add margin.
            // This prevents the ad from becoming sticky if it won't have very much
            // room to scroll before if becomes unsticky again.
            if (stickyScrollHeight > minimumStickyScrollHeight) {
              wrapper.className = 'stuck'
            } else {
              wrapper.className = ''
            }
          } else {
            // Stop stop element. Make sticky.
            wrapper.className = 'stuck'
          }
        } else {
          wrapper.className = ''
        }
      },
      offset: stickyOffset
    })

    if (stopElement !== null) {
      // Waypoint to unstick at footer
      const offsetBuffer = 5
      this.slotStopWaypoints[slot.adUnitId] = new Waypoint({
        element: stopElement,
        handler(direction): void {
          if (direction === 'down') {
            // Unstick on way down
            wrapper.className = ''
          } else {
            const stickyScrollHeight =
              this.element.offsetTop - placeholder.offsetTop
            const minimumStickyScrollHeight =
              placeholder.offsetHeight * 2 + PIXEL_BUFFER

            // Return to sticky if enough room. Extra padding in case we add margin.
            if (stickyScrollHeight > minimumStickyScrollHeight) {
              wrapper.className = 'stuck'
            } else {
              wrapper.className = ''
            }
          }
        },
        offset: adHeight + offsetBuffer + stickyOffset
      })
    }

    // Auto recalculate waypoints every second for 2 minutes. Beyond that they likely left page.
    this.recalculate[slot.id] = setInterval(() => {
      Waypoint.Context.refreshAll()
    }, setIntervalTime)

    setTimeout(() => {
      clearInterval(this.recalculate[slot.id])
    }, clearIntervalTimeout)
  }

  private destroyOldWaypoints(slot): void {
    if (this.slotWaypoints[slot]) {
      this.slotWaypoints[slot].destroy()
    }
    if (this.slotStopWaypoints[slot]) {
      this.slotStopWaypoints[slot].destroy()
    }
  }
}

function wrapElement(el, wrapper): void {
  el.parentNode.insertBefore(wrapper, el)
  wrapper.appendChild(el)
}
